import type { ChangeEvent, Dispatch, MouseEvent } from 'react';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import { VpButton, VpFormControl, VpFormHelper, VpFormLabel, VpIcon, VpIconButton, VpTextarea } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { fetchItem } from '@/app/actions/server';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { GenericDialog } from '~/components/commons/genericDialog/GenericDialog';
import { EventCode } from '~/model/MailType';
import { convertLinebreaksForMail } from '~/utils/utils';
import { MailSuccess } from './MailSuccess';

export const ReportCommentAbuse = ({
  subitem,
}: {
  subitem: AnswerType & CommentType;
}) => {
  const t = useTranslations();

  const { user } = useAuthenticatedContext();

  const [openAbuseReport, setOpenAbuseReport] = useState(false);
  const [openAbuseReportMail, setOpenAbuseReportMail] = useState(false);
  const [body, setBody] = useState('');
  const [abuseType, setAbuseType] = useState(
    t('ABUSE.FORM_COMMENT.TYPE.TYPE1'),
  );

  const [hasPosted, setHasPosted] = useState(false);
  const [loading, setLoading] = useState(false);

  const closeModal = (): void => {
    setOpenAbuseReportMail(false);
    setAbuseType(t('ABUSE.FORM_COMMENT.TYPE.TYPE1'));
    setBody('');
    setHasPosted(false);
    setLoading(false);
  };

  const canBeSubmitted: boolean = body.length > 0;

  const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!canBeSubmitted)
      return;
    setLoading(true);

    const formatBody = `${t(
      'ABUSE.FORM_COMMENT.MAIL.INFO1',
    )} ${abuseType} \n ${t(
      'ABUSE.FORM_COMMENT.MAIL.INFO4',
    )} \n ${convertLinebreaksForMail(body)}`;

    await fetchItem({
      pathname: 'send_mail',
      method: 'POST',
      body: {
        eventCode: EventCode.report_of_abuse,
        comment: subitem['@id'],
        attributes: {
          MESSAGE: formatBody,
        },
      },
    });

    setHasPosted(true);
    setLoading(false);
  };

  const handleInputTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    changeData: Dispatch<string>,
  ): void => {
    changeData(e.target.value);
  };

  const causeAbuseButton = type => (
    <VpButton
      variant={abuseType === type ? 'primary' : 'tertiary'}
      size="small"
      onClick={() => {
        setAbuseType(type);
      }}
    >
      {type}
    </VpButton>
  );

  const content = (
    <div className="popin contact-popin">
      {hasPosted
        ? (
            <MailSuccess closeModal={closeModal} />
          )
        : (
            <>
              <div className="abuse__filter">
                {t('ABUSE.FORM_COMMENT.TYPE_SELECTION')}
                <div className="abuse__button">
                  {causeAbuseButton(t('ABUSE.FORM_COMMENT.TYPE.TYPE1'))}
                  {causeAbuseButton(t('ABUSE.FORM_COMMENT.TYPE.TYPE2'))}
                  {causeAbuseButton(t('ABUSE.FORM_COMMENT.TYPE.TYPE3'))}
                  {causeAbuseButton(t('ABUSE.FORM_COMMENT.TYPE.TYPE4'))}
                </div>
              </div>
              <div data-testid="addComment" className="abuse__comment">
                <VpFormControl required>
                  <VpFormLabel>{t('ABUSE.FORM_COMMENT.MAIL.ADD_COMMENT')}</VpFormLabel>
                  <VpTextarea
                    id="body"
                    name="body"
                    data-testid="abuseComment"
                    defaultValue={body}
                    onChange={e => handleInputTextChange(e, setBody)}
                    maxLength={2000}
                  />
                  <VpFormHelper>{t('DISCUSSION.CHARACTERS_MAX')}</VpFormHelper>
                </VpFormControl>
              </div>
            </>
          )}
    </div>
  );

  const commentMenu = useRef<HTMLSpanElement>(undefined);

  const handleClick = (e: Event) => {
    if (
      commentMenu?.current
      && !commentMenu.current.contains(e.target as Node)
    ) {
      setOpenAbuseReport(false);
    }
  };

  useEffect(() => {
    // add when mounted
    document.addEventListener('mousedown', handleClick);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  return (
    <>
      {subitem?.created_by['@id'] !== user['@id'] && (
        <span ref={commentMenu} className="comment-menu">
          <VpIconButton
            data-testid="abuseButton"
            size="small"
            variant="tertiary"
            onClick={() => {
              setOpenAbuseReport(true);
            }}
          >
            <VpIcon name="more-2" />
          </VpIconButton>
          {openAbuseReport && (
            <span className="comment-menu__flyout flyout flyout--top flyout--left">
              <span className="flyout__inner">
                <VpButton
                  data-testid="abuseReport"
                  size="small"
                  startSlot={<VpIcon name="warning-error" size={16} />}
                  variant="tertiary"
                  onClick={() => {
                    setOpenAbuseReport(false);
                    setOpenAbuseReportMail(true);
                  }}
                >
                  {t('ABUSE.FORM_COMMENT.ACTION')}
                </VpButton>
              </span>
            </span>
          )}
        </span>
      )}
      <GenericDialog
        isOpen={openAbuseReportMail}
        isClosable
        onClose={closeModal}
        content={content}
        isLarge={!hasPosted}
        isLoading={loading}
        title={(
          <div className="title-abuse">
            <VpIcon name="warning-error" />
            <span>{t('ABUSE.FORM_COMMENT.MAIL.BOX_TITLE')}</span>
          </div>
        )}
        primaryButtonClick={handleSubmit}
        primaryButtonTitle={t('GLOBAL.CONFIRM')}
        secondaryButtonTitle={t('GLOBAL.CANCEL')}
        secondaryButtonClick={closeModal}
        hasFooter={!hasPosted}
        maskClosable={false}
      />
    </>
  );
};
